import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudInfoBox, MudModal } from '@egov-moldova/mud-react';
import { ExpiryMonthInput, formatExpiry, onModalDismiss } from '@simsm/ui-shared';
import { apiErrorKey } from '../../../app/api-client';
import { requestCorrection } from '../../../features/corrections/api';
import { CorrectionRequestReview } from '../corrections/CorrectionRequestReview';
import '../reporting/expiry-month.css';

interface Props {
  open: boolean;
  medicineCode: string;
  // The name the confirmation step reads back; '' falls back to the code.
  medicineName: string;
  lot: string;
  currentExpiry: string;
  // 'DAY' | 'MONTH': how the lot's expiry was reported.
  currentExpiryPrecision?: string | undefined;
  onClose: () => void;
  onSubmitted: () => void;
}

// No reason is asked for here either (owner ruling 2026-09-15): caiet UC51 and
// UC22 name none, "Solicitare nouă" dropped its field, and no screen shows one
// to the approver any more — so collecting it here would be text nobody reads.
export function CorrectionExpiryModal({
  open,
  medicineCode,
  medicineName,
  lot,
  currentExpiry,
  currentExpiryPrecision,
  onClose,
  onSubmitted,
}: Props): JSX.Element {
  const { t } = useTranslate();
  const queryClient = useQueryClient();
  const [proposedExpiry, setProposedExpiry] = useState('');
  // UC51-05: the form, then its explicit confirmation; only the second POSTs
  // (docs/shape-ops-expiry-correction-gate.md D5).
  const [step, setStep] = useState<'form' | 'review'>('form');
  // The key, not the sentence: a language switch while the refusal is on
  // screen must re-render it in the new language.
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      requestCorrection({
        medicineCode,
        lot,
        proposedExpiry,
      }),
    onSuccess: () => {
      setErrorKey(null);
      void queryClient.invalidateQueries({ queryKey: ['operator-corrections'] });
      void queryClient.invalidateQueries({ queryKey: ['operator-submission'] });
      void queryClient.invalidateQueries({ queryKey: ['correction-eligibility'] });
      void queryClient.invalidateQueries({ queryKey: ['my-correction-counts'] });
      onSubmitted();
      onClose();
      setProposedExpiry('');
      setStep('form');
    },
    // The refusal the operator reads is ours, in their language. The server's
    // own `message` is English developer prose where it exists at all, and the
    // bare code where it does not.
    onError: (e: unknown) => {
      setErrorKey(apiErrorKey(e, 'correction.error.generic'));
    },
  });

  // spec 041 — the proposal arrives already completed to the month's last day,
  // so this exact comparison catches "March 2027" against a lot expiring
  // 31.03.2027 (caiet UC51-04, nothing to correct) and lets it through against
  // one expiring 17.03.2027, which really is a two-week change.
  const unchanged = proposedExpiry.length === 10 && proposedExpiry === currentExpiry;
  const canSubmit = proposedExpiry.length === 10 && !unchanged && !submit.isPending;
  const reviewing = step === 'review';

  return (
    // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — seven AGE
    // components emit `mudClose` bubbling+composed, so a descendant closing inside this
    // modal reaches this handler too; the guard answers only a real dismissal.
    <MudModal open={open} closeLabel={t('common.close')} onMudClose={onModalDismiss(onClose)}>
      <h2 slot="title">{reviewing ? t('correction_confirm.title') : t('correction.title')}</h2>
      {reviewing ? (
        <div className="simsm-correction-modal">
          <CorrectionRequestReview
            medicine={medicineName || medicineCode}
            lot={lot}
            currentExpiry={currentExpiry}
            currentExpiryPrecision={currentExpiryPrecision}
            proposedExpiry={proposedExpiry}
            proposedExpiryPrecision="MONTH"
          />
          {errorKey ? (
            <MudInfoBox
              emphasis="strong"
              role="alert"
              variant="warning"
              titleText={t('correction.error.title')}
            >
              {t(errorKey)}
            </MudInfoBox>
          ) : null}
        </div>
      ) : (
        <div className="simsm-correction-modal">
          <div className="simsm-correction-modal__row">
            <span className="simsm-correction-modal__label">{t('correction.label.lot')}</span>
            <code>{lot}</code>
          </div>
          <div className="simsm-correction-modal__row">
            <span className="simsm-correction-modal__label">
              {t('correction.label.current_expiry')}
            </span>
            <code>{formatExpiry(currentExpiry, currentExpiryPrecision)}</code>
          </div>
          <label className="simsm-correction-modal__field">
            <span>{t('correction.label.proposed_expiry')}</span>
            <ExpiryMonthInput
              value={proposedExpiry}
              onChange={setProposedExpiry}
              invalid={unchanged}
              ariaLabel={t('correction.label.proposed_expiry')}
              placeholder={t('expiry.month_placeholder')}
              // UC51-04: why an unchanged date cannot be sent, in the words of
              // "Solicitare nouă" (docs/shape-ops-expiry-correction-gate.md D6).
              errorText={unchanged ? t('correction_new.hint.same_date') : undefined}
            />
          </label>
          {errorKey ? (
            <MudInfoBox
              emphasis="strong"
              role="alert"
              variant="warning"
              titleText={t('correction.error.title')}
            >
              {t(errorKey)}
            </MudInfoBox>
          ) : null}
        </div>
      )}
      {reviewing ? (
        <div slot="actions" className="simsm-correction-modal__footer">
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            disabled={submit.isPending}
            onClick={() => {
              setErrorKey(null);
              setStep('form');
            }}
          >
            {t('correction_confirm.action.back')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            disabled={!canSubmit}
            onClick={() => submit.mutate()}
          >
            {t('correction_confirm.action.confirm')}
          </MudButton>
        </div>
      ) : (
        <div slot="actions" className="simsm-correction-modal__footer">
          <MudButton shape="circular" variant="neutral" size="md" onClick={onClose}>
            {t('correction.action.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            disabled={!canSubmit}
            onClick={() => {
              setErrorKey(null);
              setStep('review');
            }}
          >
            {t('correction.action.submit')}
          </MudButton>
        </div>
      )}
    </MudModal>
  );
}
